import { Button, ButtonProps, CircularProgress, styled, Typography } from '@mui/material';
import { forwardRef } from 'react';
import { DynamicIcon } from 'src/components';

const StyledButton = styled(Button, {
  name: 'InputFieldSendButton',
})(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(0, 1.5),
  minHeight: 36,
  whiteSpace: 'nowrap',
  overflow: 'hidden',

  '@container (max-width: 30rem)': {
    '&': {
      padding: theme.spacing(0, 0.75),
    },

    '.button-text': {
      display: 'none',
    },
  },
  '.MuiButtonGroup': {
    boxShadow: 'none',
  },
  '.MuiButton-startIcon': {
    margin: 0,
  },
  '.MuiButton-endIcon': {
    margin: 0,
  },

  '&.MuiButton-contained': {
    boxShadow: theme.shadows[2],
    borderColor: theme.palette.background.paper,
  },

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0, 0.75),
  },
}));

export const SendButton = forwardRef<
  HTMLButtonElement,
  ButtonProps & {
    startIcon?: string;
    type?: string;
    isLoading?: boolean;
    buttonText?: string;
  }
>(({ buttonText, isLoading, startIcon = 'send', ...props }, ref) => {
  return (
    <StyledButton
      ref={ref}
      {...props}
      variant="contained"
      startIcon={
        isLoading ? (
          <CircularProgress size="1.25rem" color="primary" />
        ) : (
          <DynamicIcon
            sx={{
              height: 'auto',
              lineHeight: '1.4rem',
              fontSize: '1.4rem !important',
              margin: {
                xs: 0,
              },
            }}
            name={startIcon}
            weight={300}
          />
        )
      }
    >
      <Typography
        className="button-text"
        variant="overline"
        sx={{
          fontWeight: 700,
          letterSpacing: (theme) => theme.typography.overline.letterSpacing,
          display: { xs: 'none', md: 'initial' },
          marginLeft: '0.5rem',
        }}
      >
        {buttonText}
      </Typography>
    </StyledButton>
  );
});
SendButton.displayName = 'SendButton';

export const SecondaryButton = forwardRef<
  HTMLButtonElement,
  ButtonProps & {
    type?: string;
    isLoading?: boolean;
    buttonText?: string;
    icon?: string;
  }
>(({ buttonText, icon, isLoading, ...props }, ref) => {
  const buttonIcon = isLoading ? (
    <CircularProgress size="1.25rem" color="primary" />
  ) : (
    icon && (
      <DynamicIcon
        sx={{
          fontSize: '1.4rem !important',
          margin: {
            xs: 0,
          },
        }}
        name={icon}
      />
    )
  );
  return (
    <StyledButton ref={ref} {...props} variant="text" endIcon={buttonIcon}>
      <Typography
        className="button-text"
        variant="overline"
        sx={{
          fontWeight: 700,
          letterSpacing: (theme) => theme.typography.overline.letterSpacing,
          marginRight: buttonIcon ? '0.5rem' : 0,
          display: { xs: 'none', md: 'initial' },
        }}
      >
        {buttonText}
      </Typography>
    </StyledButton>
  );
});
SecondaryButton.displayName = 'SecondaryButton';
